import { type ReactNode, useEffect } from 'react'
import Animated, {
	Easing,
	FadeIn,
	FadeInDown,
	ReduceMotion,
	useAnimatedStyle,
	useSharedValue,
	withRepeat,
	withTiming,
} from 'react-native-reanimated'

const STEP_MS = 70

/**
 * Things arrive, they do not appear. `Rise` brings its child up into place with a little
 * spring; given an `index`, siblings arrive one after another, which is what makes a screen feel
 * composed rather than loaded. Kept to one entrance so every screen has the same manners.
 */
export const Rise = ({
	index = 0,
	children,
	className,
}: { index?: number; children: ReactNode; className?: string }) => (
	<Animated.View
		className={className}
		entering={FadeInDown.delay(index * STEP_MS)
			.springify()
			.damping(18)
			.stiffness(160)}
	>
		{children}
	</Animated.View>
)

/** For a thing that should simply be there a beat later, without moving. */
export const Appear = ({
	delay = 0,
	children,
	className,
}: { delay?: number; children: ReactNode; className?: string }) => (
	<Animated.View className={className} entering={FadeIn.delay(delay).duration(260)}>
		{children}
	</Animated.View>
)

/** One revolution every four seconds: alive, not loading. */
const TURN_MS = 4000

/**
 * Whatever sits inside turns, slowly and forever, and holds still where the system asks for less
 * motion. The pending mark wears it here; the Home to-do wears it too, so "not yet done" moves
 * the same way everywhere.
 */
export const Turning = ({ children }: { children: ReactNode }) => {
	const turn = useSharedValue(0)

	useEffect(() => {
		turn.value = withRepeat(
			withTiming(360, {
				duration: TURN_MS,
				easing: Easing.linear,
				reduceMotion: ReduceMotion.System,
			}),
			-1
		)
	}, [turn])

	const spin = useAnimatedStyle(() => ({ transform: [{ rotate: `${turn.value}deg` }] }))

	return <Animated.View style={spin}>{children}</Animated.View>
}

/**
 * Turns its child to an angle rather than to it: a chevron that swings between pointing along a
 * row and pointing up, so the change reads as the same gesture that moved the surface. A touch
 * quicker than the thing it opens, the way a control should answer before the room does. Under
 * Reduce Motion it simply arrives at the angle.
 */
export const Swivel = ({ deg, children }: { deg: number; children: ReactNode }) => {
	const angle = useSharedValue(deg)

	useEffect(() => {
		angle.value = withTiming(deg, {
			duration: 240,
			easing: Easing.out(Easing.cubic),
			reduceMotion: ReduceMotion.System,
		})
	}, [deg, angle])

	const style = useAnimatedStyle(() => ({
		transform: [{ rotate: `${angle.value}deg` }],
	}))

	return <Animated.View style={style}>{children}</Animated.View>
}
